import { ConstantField } from '@shared/types/flowstep/mapping';
import { makeAutoObservable } from 'mobx';
import { DataField } from 'shared/providers/types';
import WithValidation, { ValidationErrorsMap } from 'src/v2/states/validation';
import { ColumnFieldMappingEditorState } from '../ColumnFieldMapping/ColumnFieldMappingEditor';
import { ConstantMappingEditorState } from './ConstantMappingEditor/ConstantMappingEditor';

type ConstantMappingsErrorKey = string;

export class ConstantMappingsState implements WithValidation<ConstantMappingsErrorKey> {
  editorStates: ConstantMappingEditorState[] = [];
  mappingFields: ColumnFieldMappingEditorState[] = [];

  constructor(constantFields: ConstantField[], mappingFields: ColumnFieldMappingEditorState[]) {
    makeAutoObservable(this);

    this.mappingFields = mappingFields;
    this.editorStates = constantFields.map<ConstantMappingEditorState>((constantField) => ({
      constantValue: constantField.value,
      fieldName: constantField.field.name,
    }));
  }

  initialize(objectFields: DataField[]) {
    if (this.editorStates.length === 0 && objectFields.length > 0) {
      this.editorStates.push({
        fieldName: objectFields[0].name,
        constantValue: '',
      });
    }
  }

  addConstant(objectFields: DataField[]) {
    this.editorStates.push({
      fieldName: objectFields[0]?.name,
      constantValue: '',
    });
  }

  deleteConstant(index: number) {
    this.editorStates.splice(index, 1);
  }

  buildContantFields(objectFields: DataField[], objectType: string): ConstantField[] {
    return this.editorStates.map<ConstantField>((editorState) => {
      const field = objectFields.find((field) => field.name === editorState.fieldName);
      return {
        field: {
          name: field?.name || '',
          type: field?.type || '',
        },
        value: editorState.constantValue,
        needConversion: false,
        objectType: objectType,
      };
    });
  }

  private validateEditors(errors: ValidationErrorsMap<string>) {
    this.editorStates.forEach((editorState, index) => {
      const anotherEditorState = this.editorStates.find(
        (anotherState) => anotherState.fieldName === editorState.fieldName,
      );
      if (anotherEditorState !== editorState) {
        errors.set(
          index.toString(),
          `The field "${editorState.fieldName}" is already used in another constant mapping.`,
        );
        return;
      }
      const mappingField = this.mappingFields.find(
        (mapping) => mapping.fieldName === editorState.fieldName && mapping.checked,
      );

      if (mappingField) {
        errors.set(
          index.toString(),
          `The field "${editorState.fieldName}" is already mapped to column "${mappingField.columnName}".`,
        );
        return;
      }

      if (!editorState.fieldName) {
        errors.set(index.toString(), `Please select a field for the constant mapping.`);
        return;
      }
    });
  }

  get validationErrors() {
    const errors = new Map<ConstantMappingsErrorKey, string>();
    this.validate(errors);
    return errors;
  }

  validate(errors: ValidationErrorsMap<string>): void {
    this.validateEditors(errors);
  }
}
